import React, { useEffect, useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../../../../../context/UserContext";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import { options } from "../util";
import { Stack, Typography, Box, Alert } from "@mui/material";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import CheckboxField from "../../../../../../components/CheckboxField";
import { TextField } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields";
import { get } from "lodash";
import employerFlowStyles from "../../styles";

export default function EligibleExpenses(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    mode,
    exitUrl,
    step,
    formik,
    onboarding,
    displayOnly,
    permissibleClassId,
  } = props;
  const {
    user,
    getEmployer,
    getEmployerPlan,
    getEmployerPlanId,
    getPermissibleClass,
  } = useContext(UserContext);
  const flowHandlers = useContext(FlowHandlersContext);
  const { errors, touched, values, setFieldValue, setValues, submitCount } =
    formik;

  const employer = getEmployer();
  const permissibleClass = getPermissibleClass(permissibleClassId);
  const employerPlanId = permissibleClass?.employerPlanId;
  const employerPlan = getEmployerPlan(employerPlanId);
  const planType = employerPlan?.type || "";

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const hasSelection = Object.values(values?.qualifiedBenefits || {}).some(
    Boolean,
  );
  const shouldShowErrors = hasAttemptedSubmit;
  const showSelectionError = shouldShowErrors && !hasSelection;

  // Bullets for each option value
  let optionBullets = {
    QUALIFIED: [
      "Reimburses insurance premiums for medical, dental, and vision coverage only.",
    ],
    NON_TAXABLE_PREMIUMS: [
      "Reimburses insurance premiums for medical, dental, and vision coverage only.",
      "Premiums paid with pre-tax dollars (for example, through a spouse's employer-sponsored plan) are not eligible.",
    ],
    SECTION_213D: [
      "Reimburses insurance premiums for medical, dental, and vision coverage, as well as other qualifying medical expenses such as deductibles, copayments, prescription drugs, and eligible over-the-counter items.",
    ],
    NON_TAXABLE_PREMIUMS_EXPENSES: [
      "Reimburses insurance premiums for medical, dental, and vision coverage, and other qualifying medical expenses such as deductibles, copayments, prescription drugs, and eligible over-the-counter items.",
      "Premiums paid with pre-tax dollars (for example, through a spouse's employer-sponsored plan) are not eligible.",
      "(Recommended — most employers choose this option.)",
    ],
    SECTION_213D_EXCEPT: [
      "Out-of-pocket medical expenses (deductibles, copayments, prescription drugs, eligible over-the-counter medications)",
      "Only expenses not otherwise reimbursed by insurance or another health plan",
    ],
    OTHER: ["Other expenses as specified by the employer"],
  };

  // Only show "OTHER" option if it's already selected (grandfathered plans)
  const hasOtherSelected = values?.qualifiedBenefits?.OTHER === true;

  const filteredOptions = useMemo(() => {
    const allOptions = options.qualifiedBenefits?.[planType] || [];
    if (hasOtherSelected) {
      return allOptions;
    }
    return allOptions.filter((option) => option.value !== "OTHER");
  }, [planType, hasOtherSelected]);

  useEffect(() => {
    formik.setSubmitting(false);
    if (submitCount > 0) {
      formik.resetForm({ values: formik.values });
    }
    if (values.qualifiedBenefits?.["NONE"]) {
      setValues({ ...values, qualifiedBenefits: {} });
    }
    if (values.qualifiedBenefits_required === undefined) {
      setFieldValue("qualifiedBenefits_required", "");
    }
    if (values.otherBenefit_required === undefined) {
      setFieldValue("otherBenefit_required", "");
    }
  }, []);

  useEffect(() => {
    const qualifiedBenefits = values?.qualifiedBenefits || {};
    const hasQualifiedSelection =
      Object.values(qualifiedBenefits).some(Boolean);
    const nextQualifiedRequiredValue = hasQualifiedSelection ? "yes" : "";
    if (
      (values?.qualifiedBenefits_required || "") !== nextQualifiedRequiredValue
    ) {
      setFieldValue("qualifiedBenefits_required", nextQualifiedRequiredValue);
    }

    const hasOtherSelection = qualifiedBenefits.OTHER === true;
    const nextOtherRequiredValue = hasOtherSelection ? "yes" : "no";
    if ((values?.otherBenefit_required || "no") !== nextOtherRequiredValue) {
      setFieldValue("otherBenefit_required", nextOtherRequiredValue);
    }
  }, [values?.qualifiedBenefits]);

  useEffect(() => {
    if (shouldShowErrors && errors?.qualifiedBenefits) {
      formik.setFieldTouched("qualifiedBenefits", true, false);
    }
  }, [shouldShowErrors, errors?.qualifiedBenefits]);

  useEffect(() => {
    if (shouldShowErrors && errors?.qualifiedBenefits_required) {
      formik.setFieldTouched("qualifiedBenefits_required", true, false);
    }
  }, [shouldShowErrors, errors?.qualifiedBenefits_required]);

  function buildTouchedFromErrors(errors) {
    if (!errors || typeof errors !== "object") {
      return true;
    }

    if (Array.isArray(errors)) {
      return errors.map((item) => buildTouchedFromErrors(item));
    }

    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = buildTouchedFromErrors(errors[key]);
      return acc;
    }, {});
  }

  async function handleNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setHasAttemptedSubmit(true);

    const errors = await formik.validateForm();
    const hasErrors = Object.keys(errors).length > 0;
    const qualifiedBenefits = values?.qualifiedBenefits || {};
    const hasQualifiedSelection =
      Object.values(qualifiedBenefits).some(Boolean);

    if (hasErrors || !hasQualifiedSelection) {
      formik.setTouched(buildTouchedFromErrors(errors), true);
      return false;
    }

    try {
      await formik.submitForm();
    } catch {
      /**
       * The save was rejected by the backend — submitPlan has already shown
       * the message. Stay on this step rather than advancing past a save
       * that did not happen.
       */
      return false;
    }

    if (flowHandlers?.onNext) {
      await flowHandlers.onNext(formik.values);
    }

    return false;
  }

  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        {/* ACH info banner */}
        {planType === "QSEHRA" && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fc",
              border: "1px solid #ACBEE8",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
              Impact on Reimbursement Method
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Excluding Taxable Premiums allows reimbursements to be automated
              through ACH.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Otherwise, employers <strong>must</strong> reimburse employees
              directly, usually through payroll, based on a monthly
              reimbursement report provided by Salusion.
            </Typography>
          </Box>
        )}
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Eligible Expenses
        </Typography>
        <Typography>
          Select the expenses your {planType} will reimburse
        </Typography>

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          <Stack spacing="16px">
            {filteredOptions.map((option) => (
              <Box key={option.value}>
                <CheckboxField
                  formik={formik}
                  type="radio"
                  name="qualifiedBenefits"
                  options={[option]}
                  displayKey="display"
                  valueKey="value"
                  disabled={displayOnly}
                  showError={false}
                />
                {optionBullets[option.value] && (
                  <Box
                    component="ul"
                    sx={{ margin: "4px 0 0 28px", paddingLeft: "20px" }}
                  >
                    {optionBullets[option.value].map((bullet, index) => (
                      <Typography
                        component="li"
                        key={index}
                        sx={{
                          fontSize: "14px",
                          color: "#465578",
                          marginBottom: "2px",
                        }}
                      >
                        {bullet}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>

          {showSelectionError && (
            <div className="error-message-fields">
              Please select at least one eligible expense before continuing
            </div>
          )}

          <Field name="qualifiedBenefits_required">
            {({ field }) => (
              <input {...field} type="hidden" value={field.value || ""} />
            )}
          </Field>

          <Field name="otherBenefit_required">
            {({ field }) => (
              <input {...field} type="hidden" value={field.value || ""} />
            )}
          </Field>

          <TextField
            formik={formik}
            type="text"
            name="otherBenefit"
            label="Enter other expenses that qualify"
            showError={shouldShowErrors}
            hidden={({ values }) => !values?.qualifiedBenefits?.OTHER}
            validation={{ anyChar: true }}
          />
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );
}
